// User preference persistence.
//
// Tries the ai-coworker middleware first. Falls back to localStorage when:
//   - the endpoint is unreachable (pre-auth ops SPA, offline dev, CI)
//   - the server responds 401 (no session yet — M5 wires the shell)
//   - the server responds 4xx/5xx (network flake, Odoo down, bad payload)
// The fallback keeps the UI usable and the preferences store functional;
// once the ops SPA has auth and the endpoint is live, real persistence
// takes over automatically with no code change.

// NavSection is widened to the 10-section model in Sprint 13a; keeping
// the type loose here (string keys) avoids a circular import from the
// shell module-system into a low-level lib module. The shell store
// narrows the keys back down via its own type when reading.
export type SidebarSectionCollapsedMap = Partial<Record<string, boolean>>

// Sprint 13b — per-user sidebar item ordering, keyed by NavSection.
// Each value is an ordered list of module ids. Modules not in the list
// fall back to manifest `navOrder` ascending after the listed ones.
export type SidebarOrderMap = Partial<Record<string, ReadonlyArray<string>>>

// Per-user sidebar CATEGORY (section) ordering. Unlike `SidebarOrderMap`
// there's only one top-level order, so this is a flat list of
// `NavSection` string keys rather than a map. Sections not listed fall
// back to the shell's default `SECTION_ORDER` position after the listed
// ones. Independent of `sidebarOrder` — resetting one does not clear
// the other.
export type SidebarSectionOrderList = ReadonlyArray<string>

// Sprint 17 — per-module DataTable column state, keyed by `moduleId`
// (the prop the table primitive accepts). `order` is the user-preferred
// column id sequence; `sizing` is column id → width in px. Either may
// be empty / partial — DataTable falls back to the columnWidths prop /
// manifest column order for missing keys.
export type TableColumnState = {
  order: ReadonlyArray<string>
  sizing: Record<string, number>
}
export type TableColumnsMap = Partial<Record<string, TableColumnState>>

// Sprint 22 hot-fix (2026-05-08) — per-user kanban column ordering,
// keyed by the consuming module's `moduleId` (the prop the primitive
// accepts). Each value is an ordered list of stage ids. Stages not
// present in the list fall back to the consumer's canonical STAGES
// order after the listed ones. Mirrors `sidebarOrder` shape exactly.
export type KanbanStageOrderMap = Partial<Record<string, ReadonlyArray<string>>>

// Table row-grouping (group-by-stage). Keyed by the consuming module's
// `moduleId`. `tableGroupBy` records the chosen group-by field id per module
// ('none' / 'stage' / module-specific); `tableCollapsedGroups` records which
// group keys the user has collapsed. Both empty by default. Mirrors the
// `kanbanStageOrder` shape (backend accepts unknown keys via `extra="allow"`,
// no migration).
export type TableGroupByMap = Partial<Record<string, string>>
export type TableCollapsedGroupsMap = Partial<Record<string, ReadonlyArray<string>>>

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system'
  density: 'comfortable' | 'compact' | 'spacious'
  sidebarCollapsed: boolean
  /**
   * Sprint 13a — per-section collapse state. Keyed by `NavSection`
   * (e.g., `{ sales: true }` collapses the Sales section). Section keys
   * absent from this map default to expanded (or to whatever the user's
   * role-based default-collapsed says, which the shell computes at
   * render time and uses only when the persisted map is empty for that
   * section).
   */
  sidebarSectionsCollapsed: SidebarSectionCollapsedMap
  /**
   * Sprint 13b — per-user module ordering within each section. Keyed by
   * `NavSection`. Modules listed sort to the front in the given order;
   * any module accessible to the user but not listed falls back to its
   * manifest `navOrder` ascending after the listed ones. An empty map
   * (default) preserves the manifest-defined order for every section.
   * "Reset sidebar order" on Settings clears the map.
   */
  sidebarOrder: SidebarOrderMap
  /**
   * Per-user sidebar CATEGORY ordering — an ordered list of `NavSection`
   * keys. Sections not listed fall back to the default `SECTION_ORDER`
   * position after the listed ones. An empty list (default) preserves
   * the default section order. "Reset category order" on Settings
   * clears this list; it's independent of `sidebarOrder` (item order
   * within a section) — resetting one never touches the other.
   */
  sidebarSectionOrder: SidebarSectionOrderList
  /**
   * Sprint 17 — per-user DataTable column state, keyed by `moduleId`.
   * Each value carries the user's preferred column order and per-column
   * widths. The DataTable primitive seeds initial state from the
   * `columnWidths` prop the consumer passes; user resizes / reorders
   * mutate this map, which `schedulePersist` PATCHes to the server.
   * "Reset columns" on a module's toolbar overflow menu clears that
   * single moduleId entry; reset doesn't touch the rest.
   */
  tableColumns: TableColumnsMap
  /**
   * Sprint 22 hot-fix (2026-05-08) — per-user kanban column ordering,
   * keyed by the consuming module's `moduleId`. Empty map (default)
   * preserves the canonical STAGES order; user drags via the grip
   * handle on the column header to customize. Reset behavior follows
   * the `sidebarOrder` precedent.
   */
  kanbanStageOrder: KanbanStageOrderMap
  /**
   * Table row-grouping — the chosen "Group by" field per module
   * (e.g. `{ 'sales-inbox': 'stage' }`). Absent / 'none' → flat table.
   */
  tableGroupBy: TableGroupByMap
  /**
   * Per-module set of collapsed group keys for the grouped table view.
   * Empty (default) → all groups expanded.
   */
  tableCollapsedGroups: TableCollapsedGroupsMap
}

const STORAGE_KEY = 'spi-ops-preferences'

function apiBase(): string {
  // VITE_API_BASE points at the ai-coworker middleware. Dev default is
  // http://localhost:8000 so the ops SPA on :3000 can reach it in Docker
  // compose. If unset (standalone preview), API calls short-circuit.
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? ''
}

async function fetchFromApi(): Promise<Partial<UserPreferences> | null> {
  const base = apiBase()
  if (!base) return null
  try {
    const res = await fetch(`${base}/api/user/preferences`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    const json = (await res.json()) as Partial<UserPreferences>
    return sanitize(json)
  } catch {
    return null
  }
}

async function saveToApi(prefs: UserPreferences): Promise<boolean> {
  const base = apiBase()
  if (!base) return false
  try {
    const res = await fetch(`${base}/api/user/preferences`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(prefs),
    })
    return res.ok
  } catch {
    return false
  }
}

function readLocal(): Partial<UserPreferences> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return sanitize(JSON.parse(raw) as Partial<UserPreferences>)
  } catch {
    return {}
  }
}

function writeLocal(prefs: UserPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // storage quota / private mode — non-fatal, next session sees defaults
  }
}

export async function fetchPreferences(): Promise<Partial<UserPreferences>> {
  const fromApi = await fetchFromApi()
  if (fromApi !== null) return fromApi
  return readLocal()
}

export async function savePreferences(prefs: UserPreferences): Promise<void> {
  // Mirror to localStorage regardless — keeps dev-offline + API-fail paths
  // seeing recent values without a round trip.
  writeLocal(prefs)
  await saveToApi(prefs)
}

function sanitize(p: Partial<UserPreferences>): Partial<UserPreferences> {
  const out: Partial<UserPreferences> = {}
  if (p.theme === 'light' || p.theme === 'dark' || p.theme === 'system') out.theme = p.theme
  if (p.density === 'comfortable' || p.density === 'compact' || p.density === 'spacious') {
    out.density = p.density
  }
  if (typeof p.sidebarCollapsed === 'boolean') out.sidebarCollapsed = p.sidebarCollapsed
  if (
    p.sidebarSectionsCollapsed &&
    typeof p.sidebarSectionsCollapsed === 'object' &&
    !Array.isArray(p.sidebarSectionsCollapsed)
  ) {
    const map: SidebarSectionCollapsedMap = {}
    for (const [key, value] of Object.entries(p.sidebarSectionsCollapsed)) {
      if (typeof value === 'boolean') map[key] = value
    }
    out.sidebarSectionsCollapsed = map
  }
  if (
    p.sidebarOrder &&
    typeof p.sidebarOrder === 'object' &&
    !Array.isArray(p.sidebarOrder)
  ) {
    const map: Record<string, ReadonlyArray<string>> = {}
    for (const [section, ids] of Object.entries(p.sidebarOrder)) {
      if (Array.isArray(ids) && ids.every((id) => typeof id === 'string')) {
        map[section] = ids as ReadonlyArray<string>
      }
    }
    out.sidebarOrder = map
  }
  if (Array.isArray(p.sidebarSectionOrder) && p.sidebarSectionOrder.every((id) => typeof id === 'string')) {
    out.sidebarSectionOrder = p.sidebarSectionOrder as SidebarSectionOrderList
  }
  if (
    p.tableColumns &&
    typeof p.tableColumns === 'object' &&
    !Array.isArray(p.tableColumns)
  ) {
    const map: Record<string, TableColumnState> = {}
    for (const [moduleId, raw] of Object.entries(p.tableColumns)) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
      const order = Array.isArray(raw.order)
        ? (raw.order as unknown[]).filter((id): id is string => typeof id === 'string')
        : []
      const sizing: Record<string, number> = {}
      if (raw.sizing && typeof raw.sizing === 'object' && !Array.isArray(raw.sizing)) {
        for (const [colId, w] of Object.entries(raw.sizing)) {
          if (typeof w === 'number' && Number.isFinite(w) && w > 0) {
            sizing[colId] = w
          }
        }
      }
      // Skip entries that produce neither a usable order nor sizing —
      // keeps the map free of empty stubs from third-party tampering.
      if (order.length === 0 && Object.keys(sizing).length === 0) continue
      map[moduleId] = { order, sizing }
    }
    out.tableColumns = map
  }
  if (
    p.tableGroupBy &&
    typeof p.tableGroupBy === 'object' &&
    !Array.isArray(p.tableGroupBy)
  ) {
    const map: TableGroupByMap = {}
    for (const [moduleId, value] of Object.entries(p.tableGroupBy)) {
      if (typeof value === 'string' && value) map[moduleId] = value
    }
    out.tableGroupBy = map
  }
  if (
    p.tableCollapsedGroups &&
    typeof p.tableCollapsedGroups === 'object' &&
    !Array.isArray(p.tableCollapsedGroups)
  ) {
    const map: Record<string, ReadonlyArray<string>> = {}
    for (const [moduleId, keys] of Object.entries(p.tableCollapsedGroups)) {
      if (Array.isArray(keys) && keys.every((k) => typeof k === 'string')) {
        map[moduleId] = keys as ReadonlyArray<string>
      }
    }
    out.tableCollapsedGroups = map
  }
  return out
}
